import { Component, useRef, useState } from "react";
import Box from '@mui/material/Box';
import { Trans } from 'react-i18next';
import Button from '@mui/material/Button';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Form, Formik } from "formik";
import OutlinedTextField from "../../../components/formFields/OutlinedTextField";
import WarningAmberOutline from '@mui/icons-material/WarningAmber';
import UnitsApi from "../../../api/UnitsApi";
import DocumentTypesApi from "../../../api/DocumentTypesApi";
import ProfileHeader from "../../../components/ProfileHeader";
import { useParams, useNavigate, NavigateFunction } from "react-router-dom";
import formInitialValues from "./FormInitialValues";
import formValidationSchema from "./FormValidationSchema";
import UploadDocumentContext from "./UploadDocumentContext";
import Fab from '@mui/material/Fab';
import SaveIcon from '@mui/icons-material/Save';
import DocumentsApi from "../../../api/DocumentsApi";
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

type State = {
  refresh: boolean,
}

type Props = {
  params?: {
    domain: string,
    id: string,
  },
  navigate?: NavigateFunction
}

function UploadDocumentForm(props: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFileName, setselectedFileName] = useState<String | null>('');

  const handleButtonClick = () => {
    if (inputRef?.current === null) return
    inputRef.current.click();
  };

  const handleFileSelected = (event: any) => {
    setselectedFileName(event.target.files[0].name);
  };

  const handleGoBack = () => {
    if (!props.navigate) return
    props.navigate(`/web/unit-detail/${props.params!.domain}/documents/${props.params!.id}`, { replace: true })
  }

  return (
    <UploadDocumentContext.Consumer>
      { context => {
        const handleChange = (values: any) => {
          const fileInput = document.querySelector('input[name="file"]') as HTMLInputElement;
          const dateInput = document.querySelector('input[name="date"]') as HTMLInputElement;
          const files = fileInput?.files || []
          context.isValid = files.length > 0 && dateInput?.value !== null
          if (!context.isValid) return
          context.formValues = {
            file: files[0] || null,
            date: dateInput.value
          }
          context.refresh()
        }

        const unitDocument = context.unit.documents.find((it: any) => it.id === props.params?.id)
        const documentType = context.documentTypes.find((it: any) => it.id === unitDocument.documentType.id)

        context.submit = (_values: any, actions: any) => {
          context.isSubmitting = true
          const fileInput = document.querySelector('input[name="file"]') as HTMLInputElement;
          const dateInput = document.querySelector('input[name="date"]') as HTMLInputElement;
          const files = fileInput?.files || []
          context.isValid = files.length > 0 && dateInput?.value !== null
          if (!context.isValid) return
          DocumentsApi.attach(context.unit.id, documentType.id, dateInput.value, unitDocument.status, files[0])
          .then(() => {
            handleGoBack()
            context.isSubmitting = false
          })
        }

        return (
          <Formik
            initialValues={formInitialValues}
            onSubmit={context.submit}
            validationSchema={formValidationSchema}
            isInitialValid={false}
          >
            {({ isSubmitting, isValid, values }) => {
              context.isSubmitting = isSubmitting
              context.isValid = isValid
              return (
                <Form id="upload-document" onChange={() => handleChange(values)}>
                  <Box sx={{marginBottom: "45px", marginTop: "18px"}}>
                  <OutlinedTextField
                    label={`screens.uploadDocument.datePlaceholder`}
                    name="date"
                    autoComplete="date"
                    fullWidth={true}
                    requiredField
                    i18n
                    {...props}
                  />
                  </Box>
                  <input
                    ref={inputRef}
                    type="file"
                    name="file"
                    onChange={handleFileSelected}
                    style={{ display: 'none' }}>
                  </input>
                  <Box className="upload-document-info-value">{selectedFileName || <Trans i18nKey={`screens.uploadDocument.fileRequired`}></Trans>}</Box>
                  <Button className="upload-document-button" startIcon={<AttachFileIcon />} onClick={handleButtonClick}>
                    <Trans i18nKey={`screens.uploadDocument.selectAFile`}></Trans>
                  </Button>
                </Form>
              )
            }}
          </Formik>
        )
      }}
    </UploadDocumentContext.Consumer>
  )
}

class UploadDocument extends Component<Props, State> {

  constructor(props: any) {
    super(props)
    this.state = {refresh: false}
  }

  componentDidMount() {
    document.body.classList.add("grey-background");
  }

  componentWillUnmount() {
    window.location.reload();
    document.body.classList.remove("grey-background");
  }

  handleGoBack = () => {
    if (!this.props.navigate) return
    this.props.navigate(`/web/unit-detail/${this.props.params!.domain}/documents/${this.props.params!.id}`, { replace: true })
  }

  render() {
    return (
      <UploadDocumentContext.Consumer>
        { context => {
          if (!this.props.params) return (<Box></Box>)
          const getUnit = function (domain: string) {
            if (domain)
              UnitsApi.get(domain)
              .then(it => {
                context.unit = it.data
                context.refresh()
              })
          }

          const getDocumentTypes = function () {
            DocumentTypesApi.filter()
            .then(it => {
              context.documentTypes = it.data
              context.refresh()
            })
          }

          context.refresh = () => this.setState({refresh: !this.state.refresh})

          if (!context.unit || !context.documentTypes.length) {
            if (!context.unit && !context.documentTypes.length) {
              getUnit(this.props.params?.domain)
              getDocumentTypes()
            }
            return (<Box></Box>)
          }

          const document = context.unit.documents.find((it: any) => it.id === this.props.params?.id)
          const documentType = context.documentTypes.find((it: any) => it.id === document.documentType.id)

          return (
            <Box>
              <ProfileHeader></ProfileHeader>
              <Box className="upload-document-title"><Trans i18nKey={`screens.uploadDocument.title`}/></Box>
              <Box className="upload-document-card" sx={{margin: '0 4%'}}>
                <Box className="upload-document-info-item">
                  <Box className="upload-document-info-key"><Trans i18nKey={`screens.uploadDocument.vehicle`}/></Box>
                  <Box className="upload-document-info-value">{context.unit.numberPlate}</Box>
                </Box>
                <Box className="upload-document-info-item" sx={{marginBottom: "56px"}}>
                  <Box className="upload-document-info-key"><Trans i18nKey={`screens.uploadDocument.idDocument`}/></Box>
                  <Box className="upload-document-info-value">{documentType.name}</Box>
                </Box>
                <Box className="upload-document-field-title"><Trans i18nKey={`screens.uploadDocument.addDueDate`}/></Box>
                <UploadDocumentForm params={this.props.params} navigate={this.props.navigate}/>
                <Box className="upload-document-alert">
                  <Box className="upload-document-alert-title-section">
                    <WarningAmberOutline className="upload-document-alert-icon"/>
                    <Box className="upload-document-alert-title"><Trans i18nKey={`screens.uploadDocument.warningTitle`}/></Box>
                  </Box>
                  <Box><Trans i18nKey={`screens.uploadDocument.warningText`}/></Box>
                </Box>
              </Box>
              <Box className="bottom-nav">
                <IconButton className="bottom-nav-text-icon" onClick={this.handleGoBack}>
                  <ArrowBackIcon />
                </IconButton>
                <Box sx={{flexGrow: "1"}}/>
                <Fab className="bottom-nav-text-fab" variant="extended" disabled={!context.isValid || context.isSubmitting} onClick={context.submit}>
                  <Trans i18nKey={`screens.appBottomNav.saveButton`}></Trans>
                  <SaveIcon/>
                </Fab>
              </Box>
            </Box>
          )
        }}
      </UploadDocumentContext.Consumer>
    )
  }
 
}

function withParams(Component: any) {
  return (props: Props) => <Component {...props} params={useParams()} navigate={useNavigate()} />;
}

export default withParams(UploadDocument);